<template>
  <div class="bg-white rounded-lg pb-5 px-6 mt-5">
    <div class="flex items-center gap-2 mb-6">
      <div class="w-3 h-3 bg-orange-500 rounded-full"></div>
      <h2 class="text-xl font-semibold text-gray-900">Pending Sessions ({{ sessions.length }})</h2>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div
        v-for="session in sessions"
        :key="session.id"
        @click="openSessionDetails(session)"
        class="bg-white border-2 border-orange-800 rounded-lg p-6 shadow-[4px_4px_0_0_rgba(154,52,18,1)] hover:scale-102 transition-all duration-300 cursor-pointer"
      >
        <!-- Session Header -->
        <div class="mb-4">
          <!-- Desktop: Student Info + Status Tags in same row -->
          <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <!-- Student Info -->
            <div class="flex items-center gap-3">
              <UserAvatar
                :src="session.studentProfileImage"
                :name="session.studentName"
                size="md"
              />
              <div class="flex-1">
                <h4 class="font-medium text-gray-900">{{ session.studentName }}</h4>
              </div>
            </div>
            
            <!-- Status Tags - Desktop: upper right, Mobile: below -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getStatusClasses(session.status)"
              >
                {{ "Pending" }}
              </span>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getTypeClasses(session.type)"
              >
                {{ session.type === "face-to-face" ? "Face-to-Face" : "Online" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Session Details -->
        <div class="space-y-3 mb-4">
          <div
            class="flex justify-between border-2 border-blue-800 bg-blue-50/50 rounded-lg py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-blue-800">
              <CalendarIcon class="w-4 h-4" />
              <span class="font-semibold">{{ session.date }}</span>
            </div>

            <div class="flex items-center gap-2 text-sm text-blue-800">
              <ClockIcon class="w-4 h-4" />
              <span class="font-semibold">{{ session.time }}</span>
            </div>
          </div>

          <div class="flex items-center gap-2 text-sm text-gray-800 px-4">
            <BookOpenIcon class="w-4 h-4" />
            <span class="font-semibold">{{ session.subject }}</span>
          </div>

          <div
            v-if="session.type === 'online'"
            class="border border-blue-200 bg-blue-50/50 rounded-sm py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-blue-700 mb-2">
              <VideoCameraIcon class="w-4 h-4" />
              <span class="font-semibold">Meeting Link:</span>
            </div>
            <div class="text-sm text-gray-600 italic">
              The meeting link will be generated once approved.
            </div>
          </div>

          <div
            v-if="session.location"
            class="flex items-center border border-orange-200 bg-orange-50/50 rounded-sm py-2 px-4 gap-2 text-sm text-orange-700 min-w-0"
          >
            <MapPinIcon class="w-4 h-4 flex-shrink-0" />
            <span class="truncate" :title="session.location">{{ session.location }}</span>
          </div>

          <div
            class="text-sm text-gray-600 border border-gray-200 bg-gray-50/50 rounded-sm py-2 px-4"
          >
            <span class="font-medium">Notes:</span> {{ session.notes }}
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2">
          <button
            @click.stop="openAcceptModal(session)"
            class="flex-1 px-3 py-2 bg-white border-2 border-green-800 text-green-800 rounded-md shadow-[4px_4px_0_0_rgba(21,128,61,1)] hover:bg-green-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
          >
            <CheckCircleIcon class="w-4 h-4" />
            Accept
          </button>
          <button
            @click.stop="openDeclineModal(session)"
            class="flex-1 px-3 py-2 bg-white border-2 border-red-800 text-red-800 rounded-md shadow-[4px_4px_0_0_rgba(153,27,27,1)] hover:bg-red-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
          >
            <XCircleIcon class="w-4 h-4" />
            Decline
          </button>
        </div>

        <!-- Message Button -->
        <div class="mt-3">
          <button
            @click.stop="openMessagesView(session)"
            :disabled="isLoadingMessage === session.id"
            class="w-full px-3 py-2 bg-white border-2 border-blue-800 text-blue-800 rounded-md shadow-[4px_4px_0_0_rgba(30,64,175,1)] hover:bg-blue-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div v-if="isLoadingMessage === session.id" class="animate-spin rounded-full h-4 w-4 border-2 border-blue-800 border-t-transparent"></div>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            {{ isLoadingMessage === session.id ? 'Loading...' : 'Message' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Session Details Modal -->
    <SessionDetailsModal
      :isOpen="isModalOpen"
      :session="modalSelectedSession"
      @close="closeModal"
      @navigateToFeedback="() => {}"
      @openMessages="() => {}"
    />

    <!-- Confirmation Modals -->
    <ConfirmationModal
      :is-open="showAcceptModal"
      type="accept"
      title="Accept Session"
      message="Are you sure you want to accept this tutoring session?"
      :session-details="selectedSession || undefined"
      :is-loading="isProcessing"
      @confirm="handleAcceptSession"
      @cancel="closeModals"
    />

    <ConfirmationModal
      :is-open="showDeclineModal"
      type="decline"
      title="Decline Session"
      message="Are you sure you want to decline this tutoring session?"
      :session-details="selectedSession || undefined"
      :is-loading="isProcessing"
      @confirm="handleDeclineSession"
      @cancel="closeModals"
    />
    
    <!-- Session Toast Container -->
    <SessionToast />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import {
  CalendarIcon,
  ClockIcon,
  BookOpenIcon,
  VideoCameraIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import SessionDetailsModal from "@/components/Student-Tutor/SessionDetailsModal.vue";
import {
  createTutoringSessionEvent,
  isGoogleCalendarConnected,
} from "@/services/googleCalendarApi";
import { auth, db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useTutorSessionStore } from "@/composables/useTutorSessionStore";
import {
  sendSessionApprovedEmail,
  sendSessionDeclinedEmail,
  formatSessionDataForEmail,
} from "@/services/sessionEmailService";
import { logDebug, logError, logSuccess, logInfo } from "@/utils/secureLogger";
import type { PendingSession } from "@/types/session";
import UserAvatar from "@/components/UserAvatar.vue";
import SessionToast from "@/components/Student-Tutor/SessionToast.vue";

interface Props {
  sessions: PendingSession[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  confirm: [session: PendingSession];
  decline: [session: PendingSession];
}>();

// Use tutor session store
const { updateSessionStatus, updateSessionMeetingLink } = useTutorSessionStore();

// Initialize router at top level
const router = useRouter();

// Modal state
const showAcceptModal = ref(false);
const showDeclineModal = ref(false);
const selectedSession = ref<PendingSession | null>(null);
const isProcessing = ref(false);

// Session details modal state
const isModalOpen = ref(false);
const modalSelectedSession = ref<any>(null);

// Message loading state
const isLoadingMessage = ref<string | null>(null); // Store session ID being loaded

const getStatusClasses = (status: string) => {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-800 border-2 border-green-800";
    case "pending":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const getTypeClasses = (type: string) => {
  switch (type) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const copyMeetingLink = async (meetingLink: string) => {
  try {
    await navigator.clipboard.writeText(meetingLink);
  } catch (err) {
    logError("Failed to copy meeting link", err instanceof Error ? err : new Error(String(err)));
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = meetingLink;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};

// Modal handlers
const openAcceptModal = (session: PendingSession) => {
  selectedSession.value = session;
  showAcceptModal.value = true;
};

const openDeclineModal = (session: PendingSession) => {
  selectedSession.value = session;
  showDeclineModal.value = true;
};

const closeModals = () => {
  showAcceptModal.value = false;
  showDeclineModal.value = false;
  selectedSession.value = null;
  isProcessing.value = false;
};

// Session details modal handlers
const openSessionDetails = (session: any) => {
  modalSelectedSession.value = session;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  modalSelectedSession.value = null;
};

const openMessagesView = async (session: PendingSession) => {
  try {
    // Set loading state for this specific session
    isLoadingMessage.value = session.id;
    
    logInfo('Starting conversation from pending session', { sessionId: session.id });
    
    // Import the messaging permission service
    const { messagingPermissionService } = await import('@/services/messagingPermissionService');
    
    // Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert('Please log in to send messages');
      isLoadingMessage.value = null;
      return;
    }

    // Create conversation from session context
    const result = await messagingPermissionService.createConversationFromSession(
      session.id,
      currentUser.uid
    );

    if (!result.success) {
      alert(result.reason || 'Unable to start conversation');
      isLoadingMessage.value = null;
      return;
    }

    // Get the student's information for CometChat
    const studentId = session.studentId || session.studentName;
    const studentName = session.studentName;
    
    if (!studentId) {
      alert('Unable to identify the student for messaging');
      return;
    }
    
    // Note: CometChat initialization and user creation will be handled by the messaging view

    // Navigate to tutor messages view with the specific conversation
    router.push({
      path: '/tutor-messages',
      query: { 
        conversation: result.conversationId,
        contact: studentId
      }
    });
    
    logSuccess('Successfully navigated to messaging with student', { 
      studentId, 
      conversationId: result.conversationId 
    });
    
    // Clear loading state after successful navigation
    isLoadingMessage.value = null;
    
  } catch (error) {
    logError('Error starting conversation with student', error);
    alert('Failed to start conversation. Please try again.');
    isLoadingMessage.value = null;
  }
};

// Handle session acceptance with Google Calendar integration
const handleAcceptSession = async () => {
  if (!selectedSession.value || !auth.currentUser) return;

  isProcessing.value = true;

  try {
    // Update session status in Firestore
    const success = await updateSessionStatus(selectedSession.value.id, "confirmed");

    if (success) {
      // Check if Google Calendar is connected
      const isCalendarConnected = await isGoogleCalendarConnected(auth.currentUser.uid);

      let meetingLink = null;
      let calendarEvent = null;
      if (isCalendarConnected) {
        try {
          // Create Google Calendar event
          const sessionData = {
            studentName: selectedSession.value.studentName,
            subject: selectedSession.value.subject,
            startTime: convertToISOString(
              selectedSession.value.date,
              selectedSession.value.time,
              true
            ),
            endTime: convertToISOString(
              selectedSession.value.date,
              selectedSession.value.time,
              false
            ),
            type: selectedSession.value.type,
            location: selectedSession.value.location,
            notes: selectedSession.value.notes,
          };

          calendarEvent = await createTutoringSessionEvent(auth.currentUser.uid, sessionData);

          // Extract meeting link from Google Calendar response
          if (
            calendarEvent &&
            calendarEvent.conferenceData &&
            calendarEvent.conferenceData.entryPoints
          ) {
            const meetLink = calendarEvent.conferenceData.entryPoints.find(
              (entry: any) => entry.entryPointType === "video"
            );
            if (meetLink) {
              meetingLink = meetLink.uri;
            }
          }
        } catch (calendarError) {
          // Log the error but don't block session acceptance
          logError('Failed to create Google Calendar event - session will be accepted without calendar integration', calendarError instanceof Error ? calendarError : new Error(String(calendarError)));
          
          // Show warning to user about calendar connection issue
          // Using setTimeout to show alert after session acceptance completes
          setTimeout(() => {
            alert('⚠️ Google Calendar Connection Issue\n\nYour session was accepted successfully, but no meeting link was generated because your Google Calendar connection has expired.\n\n📋 What to do:\n• Go to Settings → Google Calendar\n• Click "Connect Google Calendar"\n• This will enable automatic meeting links for future sessions\n\nFor this session, you can manually share a meeting link with the student.');
          }, 500);
        }
      }

      // Save meeting link and calendar event ID to Firestore if available
      if (meetingLink) {
        try {
          await updateSessionMeetingLink(
            selectedSession.value.id,
            meetingLink,
            calendarEvent?.eventId
          );
        } catch (error) {
          logError("Failed to save meeting link to Firestore", error instanceof Error ? error : new Error(String(error)));
        }
      }

      // Send approval email to student
      // Get tutor name from current user or session data
      let tutorName = "Tutor"; // Default fallback

      try {
        // First try to get from Firebase auth current user
        if (auth.currentUser?.displayName) {
          tutorName = auth.currentUser.displayName;
        } else {
          // Fetch tutor data from Firestore using the current user's UID
          const tutorDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid || ''));
          if (tutorDoc.exists()) {
            const tutorData = tutorDoc.data();
            if (tutorData.firstName && tutorData.lastName) {
              tutorName = `${tutorData.firstName} ${tutorData.lastName}`;
            } else if (tutorData.firstName) {
              tutorName = tutorData.firstName;
            } else if (tutorData.displayName) {
              tutorName = tutorData.displayName;
            }
          }
        }
      } catch (error) {
        logError("Error fetching tutor name", error instanceof Error ? error : new Error(String(error)));
        // Fallback to session data
        if ((selectedSession.value as any).tutorName) {
          const sessionTutorName = (selectedSession.value as any).tutorName;
          if (typeof sessionTutorName === "string") {
            tutorName = sessionTutorName;
          } else if (sessionTutorName.firstName && sessionTutorName.lastName) {
            tutorName = `${sessionTutorName.firstName} ${sessionTutorName.lastName}`;
          }
        }
      }

      // Fetch student email from Firestore
      let studentEmail = "";
      try {
        if (selectedSession.value.studentId) {
          const studentDoc = await getDoc(doc(db, "users", selectedSession.value.studentId));
          if (studentDoc.exists()) {
            studentEmail = studentDoc.data().email || "";
          }
        }
      } catch (error) {
        logError("Failed to fetch student email", error instanceof Error ? error : new Error(String(error)));
      }

      const emailData = {
        studentEmail: studentEmail, // Use fetched email from Firestore
        studentName: selectedSession.value.studentName,
        tutorName: tutorName,
        subject: selectedSession.value.subject,
        ...formatSessionDataForEmail(selectedSession.value, "tutor"),
        meetingLink: (selectedSession.value as any).meetingLink,
      };
      logDebug(`Email data prepared for session approval: ${JSON.stringify({subject: emailData.subject, sessionDate: emailData.sessionDate})}`);

      try {
        await sendSessionApprovedEmail(emailData);
        logSuccess("Session approval email sent to student");
      } catch (emailError) {
        logError("Failed to send approval email", emailError instanceof Error ? emailError : new Error(String(emailError)));
        // Don't fail the whole process if email fails
      }

      // Emit the confirm event to parent component
      emit("confirm", selectedSession.value);

      logSuccess("Session accepted successfully");
    } else {
      logError("Failed to update session status", new Error("Session status update returned false"));
    }

    // Close modal
    closeModals();
  } catch (error) {
    logError("Error accepting session", error instanceof Error ? error : new Error(String(error)));
    closeModals();
  }
};

// Handle session decline
const handleDeclineSession = async () => {
  if (!selectedSession.value) return;

  isProcessing.value = true;

  try {
    // Update session status in Firestore with decline reason
    const success = await updateSessionStatus(
      selectedSession.value.id,
      "declined",
      "tutor-declined",
      "Session declined by tutor"
    );

    if (success) {
      // Get the current tutor's name properly (same as approval logic)
      let tutorName = "Tutor"; // Default fallback
      
      try {
        // First try to get from Firebase auth current user
        if (auth.currentUser?.displayName) {
          tutorName = auth.currentUser.displayName;
        } else {
          // Fetch tutor data from Firestore using the current user's UID
          const tutorDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid || ''));
          if (tutorDoc.exists()) {
            const tutorData = tutorDoc.data();
            if (tutorData.firstName && tutorData.lastName) {
              tutorName = `${tutorData.firstName} ${tutorData.lastName}`;
            } else if (tutorData.firstName) {
              tutorName = tutorData.firstName;
            } else if (tutorData.displayName) {
              tutorName = tutorData.displayName;
            }
          }
        }
      } catch (error) {
        logError("Error fetching tutor name for decline", error instanceof Error ? error : new Error(String(error)));
        // Fallback to session data
        if ((selectedSession.value as any).tutorName) {
          const sessionTutorName = (selectedSession.value as any).tutorName;
          if (typeof sessionTutorName === "string") {
            tutorName = sessionTutorName;
          } else if (sessionTutorName.firstName && sessionTutorName.lastName) {
            tutorName = `${sessionTutorName.firstName} ${sessionTutorName.lastName}`;
          }
        }
      }

      // Send decline email to student
      // Fetch student email from Firestore
      let studentEmail = "";
      try {
        if (selectedSession.value.studentId) {
          const studentDoc = await getDoc(doc(db, "users", selectedSession.value.studentId));
          if (studentDoc.exists()) {
            studentEmail = studentDoc.data().email || "";
          }
        }
      } catch (error) {
        logError("Failed to fetch student email", error instanceof Error ? error : new Error(String(error)));
      }

      const emailData = {
        studentEmail: studentEmail, // Use fetched email from Firestore
        studentName: selectedSession.value.studentName,
        tutorName: tutorName,
        subject: selectedSession.value.subject,
        ...formatSessionDataForEmail(selectedSession.value, "tutor"),
        declineReason: "Session declined by tutor",
      };
      logDebug(`Decline email data prepared: ${JSON.stringify({subject: emailData.subject, sessionDate: emailData.sessionDate})}`);

      try {
        await sendSessionDeclinedEmail(emailData);
        logSuccess("Session decline email sent to student");
      } catch (emailError) {
        logError("Failed to send decline email", emailError instanceof Error ? emailError : new Error(String(emailError)));
        // Don't fail the whole process if email fails
      }

      // Emit the decline event to parent component
      emit("decline", selectedSession.value);

      logSuccess("Session declined successfully");
    } else {
      logError("Failed to update session status", new Error("Session status update returned false"));
    }

    // Close modal
    closeModals();
  } catch (error) {
    logError("Error declining session", error instanceof Error ? error : new Error(String(error)));
    closeModals();
  }
};

// Helper function to convert date and time to ISO string
const convertToISOString = (date: string, time: string, isStart: boolean): string => {
  // Parse the date (assuming format like "Sat, Jan 20" or "Jan 20, 2024")
  const dateObj = new Date(date);

  // Parse the time (assuming format like "4:00 PM - 5:00 PM")
  const timeParts = time.split(" - ");
  const timeToUse = isStart ? timeParts[0] : timeParts[1];

  // Combine date and time
  const [timeStr, period] = timeToUse.split(" ");
  const [hours, minutes] = timeStr.split(":");
  let hour24 = parseInt(hours);

  if (period === "PM" && hour24 !== 12) {
    hour24 += 12;
  } else if (period === "AM" && hour24 === 12) {
    hour24 = 0;
  }

  // Create ISO string
  const isoDate = new Date(dateObj);
  isoDate.setHours(hour24, parseInt(minutes), 0, 0);

  return isoDate.toISOString();
};
</script>
